import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Users as UsersIcon,
  Plus,
  Search,
  Pencil,
  Trash2,
  Loader2,
  Shield,
  User,
  Eye,
  EyeOff,
  Key,
  Phone,
  MessageCircle,
  Printer,
  Stethoscope,
  PenLine,
  CalendarClock
} from "lucide-react";
import { toast } from "sonner";
import { printCredentialsCard } from "../lib/credentialsCard";

const Users = () => {
  const { user: currentUser, token } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printPassword, setPrintPassword] = useState("");
  const [generatingPassword, setGeneratingPassword] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    full_name: "",
    role: "nurse",
    whatsapp_number: ""
  });
  const [editFormData, setEditFormData] = useState({
    full_name: "",
    role: "",
    whatsapp_number: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const [sigDialogOpen, setSigDialogOpen] = useState(false);
  const [sigName, setSigName] = useState("");
  const [sigFile, setSigFile] = useState(null);
  const [sigPreview, setSigPreview] = useState(null);
  const [sigBusy, setSigBusy] = useState(false);

  const openSigDialog = async (user) => {
    setSelectedUser(user);
    setSigName(user.signature_name || "");
    setSigFile(null);
    setSigPreview(null);
    setSigDialogOpen(true);
    if (user.signature_path) {
      try {
        const res = await axios.get(`${API}/users/${user.id}/signature-image`, {
          headers: { Authorization: `Bearer ${token}` }, responseType: "blob"
        });
        setSigPreview(URL.createObjectURL(res.data));
      } catch { /* none */ }
    }
  };

  const handleSaveSignature = async () => {
    if (!sigName.trim()) { toast.error("اسم التوقيع في التقرير مطلوب"); return; }
    if (!sigFile && !selectedUser?.signature_path) { toast.error("يرجى رفع صورة التوقيع"); return; }
    setSigBusy(true);
    try {
      const fd = new FormData();
      fd.append("signature_name", sigName.trim());
      if (sigFile) fd.append("file", sigFile);
      await axios.post(`${API}/users/${selectedUser.id}/signature`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("تم حفظ التوقيع بنجاح");
      setSigDialogOpen(false);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل حفظ التوقيع");
    } finally {
      setSigBusy(false);
    }
  };

  const handleDeleteSignature = async () => {
    if (!window.confirm("هل تريد حذف التوقيع؟")) return;
    setSigBusy(true);
    try {
      await axios.delete(`${API}/users/${selectedUser.id}/signature`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("تم حذف التوقيع");
      setSigDialogOpen(false);
      fetchUsers();
    } catch {
      toast.error("فشل حذف التوقيع");
    } finally {
      setSigBusy(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [token]);

  useEffect(() => {
    filterUsers();
  }, [users, searchQuery, roleFilter]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error("فشل في تحميل المستخدمين");
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];
    
    if (searchQuery) {
      filtered = filtered.filter(user =>
        user.username.includes(searchQuery) ||
        user.full_name.includes(searchQuery)
      );
    }
    
    if (roleFilter !== "all") {
      filtered = filtered.filter(user => user.role === roleFilter);
    }
    
    setFilteredUsers(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username || !formData.password || !formData.full_name) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/register`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("تم إضافة المستخدم بنجاح");
      fetchUsers();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "حدث خطأ");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا المستخدم؟")) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("تم حذف المستخدم بنجاح");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "فشل في حذف المستخدم");
    }
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    setShowNewPassword(false);
    setPasswordDialogOpen(true);
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error("كلمة المرور يجب أن تكون 4 أحرف على الأقل");
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/users/${selectedUser.id}/password`, 
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("تم تغيير كلمة المرور بنجاح");
      setPasswordDialogOpen(false);
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "فشل في تغيير كلمة المرور");
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditFormData({
      full_name: user.full_name,
      role: user.role,
      whatsapp_number: user.whatsapp_number || ""
    });
    setEditDialogOpen(true);
  };

  const handleEditUser = async () => {
    setSubmitting(true);
    try {
      await axios.put(`${API}/users/${selectedUser.id}`, editFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("تم تحديث بيانات المستخدم بنجاح");
      setEditDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "فشل في تحديث البيانات");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ username: "", password: "", full_name: "", role: "technician", whatsapp_number: "" });
    setShowPassword(false);
  };

  const getRoleLabel = (role) => {
    const labels = { 
      admin: "مدير النظام", 
      maintenance_manager: "مدير الصيانة",
      it_manager: "مدير تقنية المعلومات",
      medical_manager: "المدير الطبي",
      procurement_manager: "مدير المشتريات",
      technician: "فني صيانة",
      admin_assistant: "مساعد إداري",
      data_entry: "مدخل بيانات",
      sterilization_dirty: "التعقيم - Decontamination Area",
      sterilization_packaging: "التعقيم - Packaging Area",
      sterilization_storage: "التعقيم - Sterile Area"
    };
    return labels[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: "bg-purple-100 text-purple-700",
      maintenance_manager: "bg-orange-100 text-orange-700",
      it_manager: "bg-cyan-100 text-cyan-700",
      medical_manager: "bg-green-100 text-green-700",
      procurement_manager: "bg-amber-100 text-amber-700",
      technician: "bg-blue-100 text-blue-700"
    };
    return colors[role] || "bg-slate-100 text-slate-700";
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "admin": return <Shield className="w-4 h-4" />;
      case "maintenance_manager": return <Shield className="w-4 h-4" />;
      case "it_manager": return <Shield className="w-4 h-4" />;
      case "medical_manager": return <Shield className="w-4 h-4" />;
      case "procurement_manager": return <Shield className="w-4 h-4" />;
      case "technician": return <Eye className="w-4 h-4" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="lucent-loader"><img src="/lucent-logo.png" alt="LUCENT" /><div className="lucent-loader-bar"></div></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#655dc6]">المستخدمين</h1>
          <p className="text-slate-500 mt-1">إدارة مستخدمي النظام ({users.length} مستخدم)</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#655dc6] hover:bg-[#5449a8] text-white" data-testid="add-user-btn">
              <Plus className="w-4 h-4 ml-2" />
              إضافة مستخدم
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md" dir="rtl">
            <DialogHeader>
              <DialogTitle className="text-[#655dc6]">إضافة مستخدم جديد</DialogTitle>
              <DialogDescription>أدخل بيانات المستخدم الجديد</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>الاسم الكامل *</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="مثال: أحمد محمد"
                  className="text-right"
                  data-testid="user-fullname-input"
                />
              </div>
              <div className="space-y-2">
                <Label>اسم المستخدم *</Label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="مثال: ahmed"
                  className="text-right"
                  dir="ltr"
                  data-testid="user-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label>كلمة المرور *</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="أدخل كلمة المرور"
                    className="text-right pl-10"
                    dir="ltr"
                    autoComplete="new-password"
                    data-testid="user-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>الصلاحية *</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue placeholder="اختر الصلاحية" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">مدير النظام</SelectItem>
                    <SelectItem value="maintenance_manager">مدير الصيانة</SelectItem>
                    <SelectItem value="it_manager">مدير تقنية المعلومات</SelectItem>
                    <SelectItem value="medical_manager">المدير الطبي</SelectItem>
                    <SelectItem value="procurement_manager">مدير المشتريات</SelectItem>
                    <SelectItem value="technician">فني صيانة</SelectItem>
                    <SelectItem value="sterilization_dirty">التعقيم - Decontamination Area</SelectItem>
                    <SelectItem value="sterilization_packaging">التعقيم - Packaging Area</SelectItem>
                    <SelectItem value="sterilization_storage">التعقيم - Sterile Area</SelectItem>
                    <SelectItem value="admin_assistant">مساعد إداري</SelectItem>
                    <SelectItem value="data_entry">مدخل بيانات</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>رقم واتساب (للإشعارات)</Label>
                <div className="relative">
                  <Input
                    type="tel"
                    value={formData.whatsapp_number}
                    onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                    placeholder="+966500000000"
                    className="text-right pl-10"
                    dir="ltr"
                    data-testid="user-whatsapp-input"
                  />
                  <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                </div>
                <p className="text-xs text-slate-400">أدخل الرقم بصيغة دولية مع رمز الدولة</p>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  className="flex-1 bg-[#655dc6] hover:bg-[#5449a8] text-white"
                  disabled={submitting}
                  data-testid="submit-user-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "إضافة"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setDialogOpen(false); resetForm(); }}
                  className="flex-1"
                >
                  إلغاء
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#655dc6]">
                  {users.filter(u => u.role === "admin").length}
                </p>
                <p className="text-sm text-slate-500">مدير صيانة</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#655dc6]">
                  {users.filter(u => u.role === "technician").length}
                </p>
                <p className="text-sm text-slate-500">فني صيانة</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <User className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#655dc6]">
                  {users.filter(u => u.role === "maintenance_manager" || u.role === "it_manager" || u.role === "medical_manager").length}
                </p>
                <p className="text-sm text-slate-500">مديرين الصيانة والأقسام</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="البحث في المستخدمين..."
                className="pr-10 text-right"
                data-testid="search-users-input"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="role-filter">
                <SelectValue placeholder="الصلاحية" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الصلاحيات</SelectItem>
                <SelectItem value="admin">مدير النظام</SelectItem>
                <SelectItem value="maintenance_manager">مدير الصيانة</SelectItem>
                <SelectItem value="it_manager">مدير تقنية المعلومات</SelectItem>
                <SelectItem value="medical_manager">المدير الطبي</SelectItem>
                <SelectItem value="procurement_manager">مدير المشتريات</SelectItem>
                <SelectItem value="technician">فني صيانة</SelectItem>
                <SelectItem value="admin_assistant">مساعد إداري</SelectItem>
                    <SelectItem value="data_entry">مدخل بيانات</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      {filteredUsers.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUsers.map((user) => (
            <Card key={user.id} className="border-0 shadow-sm card-hover" data-testid={`user-card-${user.id}`}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getRoleColor(user.role)}`}>
                      {getRoleIcon(user.role)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#655dc6]">{user.full_name}</h3>
                      <p className="text-sm text-slate-500">@{user.username}</p>
                      {user.whatsapp_number && (
                        <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                          <MessageCircle className="w-3 h-3" />
                          {user.whatsapp_number}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge className={getRoleColor(user.role)}>
                      {getRoleLabel(user.role)}
                    </Badge>
                    {user.is_biomed_officer && (
                      <Badge className="bg-emerald-100 text-emerald-800 border-0 gap-1" data-testid={`device-perm-badge-${user.id}`}>
                        <Stethoscope className="w-3 h-3" /> صلاحية الأجهزة
                      </Badge>
                    )}
                    {user.is_ppm_officer && (
                      <Badge className="bg-sky-100 text-sky-800 border-0 gap-1" data-testid={`ppm-perm-badge-${user.id}`}>
                        <CalendarClock className="w-3 h-3" /> مسؤول الصيانة الدورية
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-blue-500 hover:bg-blue-50"
                      onClick={() => openEditDialog(user)}
                      data-testid={`edit-user-${user.id}`}
                      title="تعديل البيانات"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={user.signature_path ? "text-teal-600 hover:bg-teal-50" : "text-slate-400 hover:bg-slate-100"}
                      onClick={() => openSigDialog(user)}
                      data-testid={`signature-user-${user.id}`}
                      title={user.signature_path ? "تعديل التوقيع" : "إضافة توقيع"}
                    >
                      <PenLine className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[#655dc6] hover:bg-purple-50"
                      onClick={() => openPasswordDialog(user)}
                      data-testid={`change-password-${user.id}`}
                      title="تغيير كلمة المرور"
                    >
                      <Key className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-green-600 hover:bg-green-50"
                      onClick={async () => {
                        setSelectedUser(user);
                        setPrintPassword("");
                        setGeneratingPassword(true);
                        setPrintDialogOpen(true);
                        try {
                          const res = await axios.post(`${API}/users/${user.id}/generate-password`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          setPrintPassword(res.data.password);
                        } catch (err) {
                          toast.error("فشل في توليد كلمة المرور");
                        } finally {
                          setGeneratingPassword(false);
                        }
                      }}
                      data-testid={`print-credentials-${user.id}`}
                      title="طباعة بيانات الدخول"
                    >
                      <Printer className="w-4 h-4" />
                    </Button>
                    {user.role !== "admin" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className={user.is_biomed_officer ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-500 hover:bg-slate-100"}
                        onClick={async () => {
                          try {
                            const res = await axios.put(`${API}/users/${user.id}/biomed-officer`, {}, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success(res.data.is_biomed_officer ? "تم منح صلاحية الأجهزة" : "تم إلغاء صلاحية الأجهزة");
                            fetchUsers();
                          } catch { toast.error("فشل التحديث"); }
                        }}
                        data-testid={`biomed-officer-${user.id}`}
                        title={user.is_biomed_officer ? "إلغاء صلاحية الأجهزة" : "منح صلاحية الأجهزة"}
                      >
                        <Stethoscope className="w-4 h-4" />
                      </Button>
                    )}
                    {user.role !== "admin" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className={user.is_ppm_officer ? "text-sky-600 hover:bg-sky-50" : "text-slate-400 hover:bg-slate-100"}
                        onClick={async () => {
                          try {
                            const res = await axios.put(`${API}/users/${user.id}/ppm-officer`, {}, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success(res.data.is_ppm_officer ? "تم منح صلاحية الصيانة الدورية — سيستلم تذكيرات الزيارات على واتسابه" : "تم إلغاء صلاحية الصيانة الدورية");
                            fetchUsers();
                          } catch { toast.error("فشل التحديث"); }
                        }}
                        data-testid={`ppm-officer-${user.id}`}
                        title={user.is_ppm_officer ? "إلغاء صلاحية الصيانة الدورية" : "منح صلاحية الصيانة الدورية (إدخال + متابعة + تذكيرات واتساب)"}
                      >
                        <CalendarClock className="w-4 h-4" />
                      </Button>
                    )}
                    {currentUser?.id !== user.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:bg-red-50"
                        onClick={() => handleDelete(user.id)}
                        data-testid={`delete-user-${user.id}`}
                        title="حذف المستخدم"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                
                <p className="text-xs text-slate-400 mt-4">
                  أنشئ في: {new Date(user.created_at).toLocaleDateString("ar-SA")}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-16 text-center">
            <UsersIcon className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-[#655dc6] mb-2">لا يوجد مستخدمين</h3>
            <p className="text-slate-500">
              {searchQuery || roleFilter !== "all" ? "لم يتم العثور على نتائج مطابقة" : "لم يتم إضافة مستخدمين بعد"}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6] flex items-center gap-2">
              <Key className="w-5 h-5" />
              تغيير كلمة المرور
            </DialogTitle>
            <DialogDescription>
              تغيير كلمة المرور للمستخدم: {selectedUser?.full_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500 mb-1">اسم المستخدم</p>
              <p className="font-medium text-[#655dc6]">@{selectedUser?.username}</p>
            </div>

            <div className="space-y-2">
              <Label>كلمة المرور الجديدة *</Label>
              <div className="relative">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="أدخل كلمة المرور الجديدة"
                  className="text-right pl-10"
                  dir="ltr"
                  autoComplete="new-password"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-slate-400">يجب أن تكون 4 أحرف على الأقل</p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleChangePassword}
                className="flex-1 bg-[#655dc6] hover:bg-[#5449a8] text-white"
                disabled={submitting}
                data-testid="submit-password-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "تغيير كلمة المرور"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setPasswordDialogOpen(false)}
                className="flex-1"
              >
                إلغاء
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6] flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              تعديل بيانات المستخدم
            </DialogTitle>
            <DialogDescription>
              تعديل بيانات: {selectedUser?.full_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>الاسم الكامل</Label>
              <Input
                value={editFormData.full_name}
                onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })}
                placeholder="أدخل الاسم الكامل"
                className="text-right"
                data-testid="edit-fullname-input"
              />
            </div>

            <div className="space-y-2">
              <Label>الصلاحية</Label>
              <Select value={editFormData.role} onValueChange={(v) => setEditFormData({ ...editFormData, role: v })}>
                <SelectTrigger data-testid="edit-role-select">
                  <SelectValue placeholder="اختر الصلاحية" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">مدير النظام</SelectItem>
                  <SelectItem value="maintenance_manager">مدير الصيانة</SelectItem>
                  <SelectItem value="it_manager">مدير تقنية المعلومات</SelectItem>
                  <SelectItem value="medical_manager">المدير الطبي</SelectItem>
                  <SelectItem value="procurement_manager">مدير المشتريات</SelectItem>
                  <SelectItem value="technician">فني صيانة</SelectItem>
                  <SelectItem value="sterilization_dirty">التعقيم - Decontamination Area</SelectItem>
                  <SelectItem value="sterilization_packaging">التعقيم - Packaging Area</SelectItem>
                  <SelectItem value="sterilization_storage">التعقيم - Sterile Area</SelectItem>
                  <SelectItem value="admin_assistant">مساعد إداري</SelectItem>
                    <SelectItem value="data_entry">مدخل بيانات</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>رقم واتساب (للإشعارات)</Label>
              <div className="relative">
                <Input
                  type="tel"
                  value={editFormData.whatsapp_number}
                  onChange={(e) => setEditFormData({ ...editFormData, whatsapp_number: e.target.value })}
                  placeholder="+966500000000"
                  className="text-right pl-10"
                  dir="ltr"
                  data-testid="edit-whatsapp-input"
                />
                <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
              </div>
              <p className="text-xs text-slate-400">أدخل الرقم بصيغة دولية مع رمز الدولة</p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleEditUser}
                className="flex-1 bg-[#655dc6] hover:bg-[#5449a8] text-white"
                disabled={submitting}
                data-testid="submit-edit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "حفظ التغييرات"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
                className="flex-1"
              >
                إلغاء
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Signature Dialog */}
      <Dialog open={sigDialogOpen} onOpenChange={setSigDialogOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6] flex items-center gap-2">
              <PenLine className="w-5 h-5" />
              توقيع المستخدم
            </DialogTitle>
            <DialogDescription>
              يظهر هذا التوقيع أسفل تقارير تقييم الأجهزة — للمستخدم: {selectedUser?.full_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>الاسم كما يظهر في التقرير *</Label>
              <Input
                value={sigName}
                onChange={(e) => setSigName(e.target.value)}
                placeholder="مثال: Eng. Ahmed Mohammed"
                data-testid="signature-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>صورة التوقيع {selectedUser?.signature_path ? "(اتركها فارغة للإبقاء على الحالية)" : "*"}</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setSigFile(f);
                  if (f) setSigPreview(URL.createObjectURL(f));
                }}
                data-testid="signature-file-input"
              />
              <p className="text-xs text-slate-400">يفضّل صورة بخلفية بيضاء أو شفافة (PNG)</p>
            </div>

            {sigPreview && (
              <div className="p-3 bg-slate-50 rounded-lg border flex justify-center">
                <img src={sigPreview} alt="التوقيع" className="max-h-24 object-contain" data-testid="signature-preview" />
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleSaveSignature}
                className="flex-1 bg-[#655dc6] hover:bg-[#5449a8] text-white"
                disabled={sigBusy}
                data-testid="save-signature-btn"
              >
                {sigBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "حفظ التوقيع"}
              </Button>
              {selectedUser?.signature_path && (
                <Button variant="outline" onClick={handleDeleteSignature} disabled={sigBusy} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-signature-btn">
                  حذف
                </Button>
              )}
              <Button variant="outline" onClick={() => setSigDialogOpen(false)}>إلغاء</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Credentials Dialog */}
      <Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6] flex items-center gap-2">
              <Printer className="w-5 h-5" />
              طباعة بيانات الدخول
            </DialogTitle>
            <DialogDescription>
              تم توليد كلمة مرور جديدة — سيُطلب من المستخدم تغييرها عند أول دخول
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500 mb-1">المستخدم</p>
              <p className="font-medium text-[#655dc6]">{selectedUser?.full_name} (@{selectedUser?.username})</p>
            </div>

            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-700 mb-1 font-medium">كلمة المرور الجديدة</p>
              {generatingPassword ? (
                <div className="flex items-center gap-2 text-green-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>جاري التوليد...</span>
                </div>
              ) : (
                <p className="font-mono text-lg font-bold text-green-800 tracking-wider" dir="ltr">{printPassword}</p>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => {
                  if (!printPassword) {
                    toast.error("انتظر حتى يتم توليد كلمة المرور");
                    return;
                  }
                  const loginUrl = "https://mnt.lucent.com.sa/login";
                  printCredentialsCard({
                    fullName: selectedUser.full_name,
                    username: selectedUser.username,
                    password: printPassword,
                    role: selectedUser.role,
                    loginUrl
                  });
                  setPrintDialogOpen(false);
                  setPrintPassword("");
                }}
                className="flex-1 bg-[#655dc6] hover:bg-[#5449a8] text-white"
                disabled={generatingPassword || !printPassword}
                data-testid="submit-print-btn"
              >
                <Printer className="w-4 h-4 ml-2" />
                طباعة
              </Button>
              <Button
                variant="outline"
                onClick={() => setPrintDialogOpen(false)}
                className="flex-1"
              >
                إلغاء
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
